
import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { COLORS, TYPE, RADIUS, CONTROL_HEIGHT, ELLIPSIS } from "../../../styles/recruiterTheme";

const SKILL_LEVELS = [
  "",
  "Beginner",
  "Elementary",
  "Intermediate",
  "Advanced",
  "Expert",
];

const ratingColor = (r) => {
  if (r <= 1) return COLORS.error;
  if (r === 2) return COLORS.warning;
  if (r === 3) return "#B7791F"; // amber, readable on white
  if (r === 4) return COLORS.success;
  return COLORS.primary;
};

// Extracted star row so each skill gets its own isolated hover state
function StarRating({ rating, onChange }) {
  const [hovered, setHovered] = useState(0);
  const display = hovered || rating;

  return (
    <Box display="flex" alignItems="center" gap={0.5} role="radiogroup" aria-label="Skill rating" flexShrink={0}>
      {[1, 2, 3, 4, 5].map((r) => (
        <Box
          key={r}
          role="radio"
          aria-checked={r === rating}
          aria-label={`${r} – ${SKILL_LEVELS[r]}`}
          tabIndex={0}
          onClick={() => onChange(r)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onChange(r); } }}
          onMouseEnter={() => setHovered(r)}
          onMouseLeave={() => setHovered(0)}
          sx={{
            fontSize: 20,
            cursor: "pointer",
            color: r <= display ? "#f59e0b" : COLORS.border,
            lineHeight: 1,
            userSelect: "none",
            transition: "color 0.15s, transform 0.1s",
            "&:hover": {
              color: "#f59e0b",
              transform: "scale(1.2)",
            },
          }}
        >
          ★
        </Box>
      ))}
    </Box>
  );
}

export default function SkillRatingInput({ value = [], onChange }) {
  const [input, setInput] = useState("");

  // Parsed resumes can hand back entries without a name/rating — drop / default them so nothing crashes.
  const skills = (Array.isArray(value) ? value : [])
    .map((s) => (typeof s === "string" ? { name: s, rating: 1 } : s))
    .filter((s) => s && typeof s.name === "string" && s.name.trim());

  const addSkill = () => {
    const name = input.trim();
    if (!name || skills.find((s) => s.name.trim().toLowerCase() === name.toLowerCase())) {
      setInput("");
      return;
    }
    onChange([...skills, { name, rating: 1 }]);
    setInput("");
  };

  const removeSkill = (idx) => onChange(skills.filter((_, i) => i !== idx));

  const updateRating = (idx, rating) =>
    onChange(skills.map((s, i) => (i === idx ? { ...s, rating } : s)));

  const handleKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addSkill();
    }
  };

  return (
    <Box>
      <Box display="flex" gap={1} mb={1.5} alignItems="center">
        <TextField
          size="small"
          fullWidth
          placeholder="Type a skill and press Enter…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
          sx={{ "& .MuiOutlinedInput-root": { height: CONTROL_HEIGHT.md } }}
        />
        <Button
          variant="outlined"
          onClick={addSkill}
          size="small"
          sx={{
            minWidth: 80,
            height: CONTROL_HEIGHT.md,
            textTransform: "none",
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          Add
        </Button>
      </Box>

      {skills.length === 0 && (
        <Typography sx={{ fontSize: TYPE.caption, color: COLORS.faint, ml: 0.5 }}>
          No skills added yet
        </Typography>
      )}

      <Box display="flex" flexDirection="column" gap={1}>
        {skills.map((s, i) => (
          <Box
            key={i}
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
              py: 0.5,
              px: 1.5,
              bgcolor: COLORS.surfaceTint,
              border: `1px solid ${COLORS.border}`,
              borderRadius: `${RADIUS.sm}px`,
            }}
          >
            <Typography title={s.name} sx={{ fontSize: TYPE.small, fontWeight: 600, color: COLORS.ink, flex: 1, ...ELLIPSIS }}>
              {s.name}
            </Typography>

            <StarRating
              rating={s.rating || 0}
              onChange={(r) => updateRating(i, r)}
            />

            <Tooltip title={SKILL_LEVELS[s.rating] || ""}>
              <Typography
                sx={{
                  fontSize: TYPE.overline,
                  fontWeight: 700,
                  minWidth: 72,
                  display: { xs: "none", sm: "block" },
                  color: ratingColor(s.rating),
                  textAlign: "right",
                }}
              >
                {SKILL_LEVELS[s.rating] || "Not rated"}
              </Typography>
            </Tooltip>

            <IconButton
              size="small"
              aria-label={`Remove ${s.name}`}
              onClick={() => removeSkill(i)}
              sx={{ color: COLORS.muted, "&:hover": { color: COLORS.error } }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
